import { ResolverState } from './resolverState';
import { ResolverHttpClient } from './httpClient';
import { ArgEntry, ResourceDefinition } from '../../resources/types';
import { Logger } from '../../devtools/logger';
import { ChangeContext } from '../../client/changeManager';

export type ResolverMeta = {
  changeContext?: ChangeContext;
};

export type ResolverRuntime<TParams, TData, TError extends Error = Error> = {
  /**
   * Publish a state update from the resolver. Expects full state objects only.
   *
   * @param state - The state to update to. If same reference, update will be ignored.
   *
   * @example
   * ```ts
   * // Publish a success state
   * runtime.next({
   *   data: { result: 1 },
   *   loading: false,
   *   error: null,
   *   stale: false,
   * });
   *
   * // in combination with `makeFunctionalNext`:
   * const next = makeFunctionalNext(runtime);
   *
   * // Set loading manually
   * next(current => ({ ...current, loading: true }))
   *
   * // With the curried transition helper
   * next(toLoading());
   * ```
   */
  next: (state: ResolverState<TData, TError>, meta?: ResolverMeta) => void;
  /**
   * Get the current state of the resource in the cache, if present.
   *
   * @example
   * ```ts
   * const state = runtime.getState();
   *
   * // state: { data: undefined, loading: true, stale: false, error: null }
   * ```
   */
  getState: () => ResolverState<TData, TError> | undefined;
  env: {
    httpClient: ResolverHttpClient;
    resource: ResourceDefinition<TParams, TData, TError>;
    instanceKey: string;
    logger?: Logger;
  };
};

export type ResolverHandle = {
  refresh: (changeContext?: ChangeContext) => void;
  detach: () => void;
};

export type Attach<TParams, TData, TError extends Error = Error> = (
  params: TParams,
  runtime: ResolverRuntime<TParams, TData, TError>
) => ResolverHandle;

export type Resolver<TParams, TData, TError extends Error = Error> = {
  name: string;
  tags: string[];
  /**
   * Param keys the resolver itself requires in the instance identity, appended
   * to the resource's own `args`. Use when the resolver reads params that the
   * resource author didn't declare — a resolver that can fetch the same data
   * from a different portal, user, or environment has to key on that, or those
   * variants collide in the cache.
   */
  args?: Array<ArgEntry<TParams>>;
  attach: Attach<TParams, TData, TError>;
};

export type AnyResolver = Resolver<any, any, any>;

export type NoParams = Record<string, never>;

export type FunctionalState<TData = any, TError extends Error = Error> =
  | ResolverState<TData, TError>
  | ((
      state?: ResolverState<TData, TError>
    ) => ResolverState<TData, TError> | void);

/**
 * A small helper that enables a functional updater form of next() (passing state objects directly is still allowed).
 *
 * Use is opt-in, but recommended if handling the standard state transitions. The
 * curried utilities exported from resolverState are built to work with this pattern.
 *
 * Returning the same state reference will no-op the update. Returning nothing (void) will also no-op.
 *
 * @example
 * ```ts
 * const resolver = {
 *   name: 'test',
 *   attach: (params, runtime) => {
 *     const next = makeFunctionalNext(runtime);
 *
 *     next(currentState => ({
 *       ...currentState,
 *       data: { result: 1 },
 *     }));
 *
 *     // or
 *
 *     next(toSuccess({ result: 1 }));
 *
 *     // or
 *
 *     next(current => {
 *       if (!current) {
 *         return; // void return to no-op
 *       }
 *
 *       // do something interesting!
 *     })
 *   }),
 * };
 * ```
 */
export const makeFunctionalNext =
  <TParams, TData, TError extends Error = Error>(
    runtime: ResolverRuntime<TParams, TData, TError>
  ) =>
  (state: FunctionalState<TData, TError>) => {
    const nextState =
      typeof state === 'function' ? state(runtime.getState()) : state;

    if (nextState) {
      runtime.next(nextState);
    }
  };
